import type { CartType } from '@/types/domains';
import type {
  PostCartParams,
  PutCartParams,
  DeleteCartParams,
} from '@/types/externals/obbsApi/carts/Params';
import type {
  ProductType,
  GetCartResponse,
  PostCartResponse,
  PutCartResponse,
  DeleteCartResponse,
  cartRelatedInformationType,
} from '@/types/externals/obbsApi/carts/Responses';
import type { NewsImage } from '@/types/externals/obbsApi/newsImages/Responses';
import type { CartResponse } from '@/types/externals/obbsApi/carts/Responses';

/** `getNewsImages` の外側のタイムアウト。内側（SSR から API への10秒）より長くする */
const NEWS_IMAGES_TIMEOUT: number = 15000;

export const useCart = () => {
  const initialCartInformation = {
    carts: [],
    amountToFreeShip: 5000,
    freeShipThreshold: 0,
    shippingFee: 0,
    totalQuantity: 0,
    totalPrice: 0,
    shippingFeeMessage: '5,000円以上で送料当社負担',
    newsImages: [],
  };

  const cartRelatedInformation = useState<cartRelatedInformationType>(
    'cartRelatedInformation',
    () => structuredClone(initialCartInformation)
  );

  const shippingFeeMessage = computed(() => {
    const { carts, shippingFee } = cartRelatedInformation.value;
    const amountToFreeShip = cartRelatedInformation.value.amountToFreeShip || 5000;

    // カートが空の場合
    if (carts.length === 0) {
      return '5,000円以上で送料当社負担';
    }

    // 送料が無料の場合
    if (shippingFee === 0) {
      return '送料当社負担';
    }

    // 送料無料まであとXX円
    return `あと${amountToFreeShip.toLocaleString()}円で送料当社負担`;
  });

  /**
   * カート情報を取り直す。
   *
   * **2本のAPIは並列で投げる。** `/news-images` はカートの中身に依存しないのに、
   * 直列だと往復ぶんだけ余計に待つ。ここは `002-session-check.global.ts` から
   * **パスが変わるたびに呼ばれ、解決するまでルートが進まない**ので、往復1回が
   * そのまま全ページの遷移の待ち時間になる（#1386）。
   *
   * **代入の順序は変えない。** `shippingFeeMessage` は `carts` / `shippingFee` /
   * `amountToFreeShip` を見る computed なので、**それらを入れたあとに読む**。
   *
   * `getCarts` / `getNewsImages` はどちらも内部で例外を握って既定値（`null` / `[]`）を
   * 返すため、`Promise.all` が reject することはない。
   */
  const fetchCartInfo = async (): Promise<void> => {
    const [cart, newsImages] = await Promise.all([getCarts(), getNewsImages()]);
    const {
      carts = [],
      amountToFreeShip = 5000,
      freeShipThreshold = 0,
      shippingFee = 0,
      totalQuantity = 0,
      totalPrice = 0,
    } = cart || {};
    cartRelatedInformation.value.carts = carts;
    cartRelatedInformation.value.freeShipThreshold = freeShipThreshold;
    cartRelatedInformation.value.shippingFee = shippingFee;
    cartRelatedInformation.value.totalQuantity = totalQuantity;
    cartRelatedInformation.value.totalPrice = totalPrice;
    cartRelatedInformation.value.amountToFreeShip = amountToFreeShip;
    cartRelatedInformation.value.shippingFeeMessage = shippingFeeMessage.value;
    cartRelatedInformation.value.newsImages = newsImages;
  };

  const getCarts = async (): Promise<GetCartResponse | null> => {
    try {
      const { data, error } = await useApiFetch<GetCartResponse>('/carts');
      if (error.value) throw new Error(`API fetch error: ${error.value.title}`);
      return data.value;
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  const postCart = async (params: PostCartParams | PostCartParams[]): Promise<PostCartResponse> => {
    try {
      const { data, error } = await useApiFetch<PostCartResponse>('/carts', {
        method: 'POST',
        body: params,
      });
      if (error.value) throw new Error(`API fetch error: ${error.value.title}`);
      await fetchCartInfo();
      return data.value;
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  const putCart = async (params: PutCartParams): Promise<PutCartResponse> => {
    try {
      const { data, error } = await useApiFetch<PutCartResponse>(`/carts/${params.cartId}`, {
        method: 'PUT',
        body: params,
      });
      if (error.value) throw new Error(`API fetch error: ${error.value.title}`);
      await fetchCartInfo();
      return data.value;
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  const deleteCart = async (params: DeleteCartParams): Promise<DeleteCartResponse> => {
    try {
      const { data, error } = await useApiFetch<DeleteCartResponse>(`/carts/${params.cartId}`, {
        method: 'DELETE',
      });
      if (error.value) throw new Error(`API fetch error: ${error.value.title}`);
      await fetchCartInfo();
      return data.value;
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  /**
   * 取得先は Nitro のキャッシュ（`usePublicCache`）。装飾なので、失敗してもページはエラーにせず `[]` を返す。
   *
   * **これだけ外側にタイムアウトを付ける。** middleware 002 がルート遷移のたびにこれを待つため、
   * クライアントからの1本が回線の停滞で止まると、その遷移が進まない。
   * 内側（SSR から API への10秒）より長くして、SSR では内側が先に切れるようにする。
   */
  const getNewsImages = async (): Promise<NewsImage[]> => {
    try {
      const { data, error } = await usePublicCache<NewsImage[]>('news-images', {
        timeout: NEWS_IMAGES_TIMEOUT,
      });
      if (error.value) throw new Error(`Failed to fetch news-images: ${error.value.message}`);
      return data.value ?? [];
    } catch (error) {
      console.error(error);
      return [];
    }
  };

  const executeUpsell = async (cartId: number): Promise<void> => {
    const url: string = `/carts/${cartId}/upsell`;
    const { error } = await useApiFetch<CartResponse>(url, {
      method: 'POST',
    });

    if (error.value) {
      throw createError({
        statusCode: error.value.status,
        statusMessage: error.value.title,
        message: error.value.detail,
        fatal: true,
      });
    }

    await fetchCartInfo();
  };

  const fetchNewsImages = async (): Promise<void> => {
    cartRelatedInformation.value.newsImages = await getNewsImages();
  };

  const getIconName = (cart: CartType.Cart): CartType.IconNameType => {
    switch (cart.type) {
      case 'product':
        return '通常';
      case 'productsRegularPattern':
        return '定期';
      case 'set':
        return cart.isCampaign ? 'CP' : 'セット';
    }
  };

  return {
    cartRelatedInformation: readonly(cartRelatedInformation),
    fetchCartInfo,
    getCarts,
    postCart,
    putCart,
    deleteCart,
    fetchNewsImages,
    getIconName,
    executeUpsell,
  };
};
